import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'

// Text sizing follows the USCIS Developer Portal readability rules for policies and
// consents: nothing below 14px (body copy is 16px), short paragraphs, no ALL-CAPS
// paragraphs, and text colors at a 4.5:1 contrast ratio or better.

const SECTIONS = [
    { id: 'fcra', label: 'FCRA Disclosure' },
    { id: 'universal_rights', label: 'Universal Right to a Copy & Dispute' },
    { id: 'summary', label: 'Summary of Rights: CFPB Summary of Rights Under the FCRA' },
    { id: 'data_verification', label: 'Data & Verification Authorization' },
    { id: 'biometric', label: 'Biometric Information Privacy Notice (BIPA & State Compliance)' },
    { id: 'esign', label: 'ESIGN Act Consent' },
]

const ALL_EXPANDED = {
    fcra: true,
    universal_rights: true,
    summary: true,
    data_verification: true,
    biometric: true,
    esign: true,
}

const TERMS_OF_USE_URL = 'https://verifiedid.co/terms-of-use'
const PRIVACY_POLICY_URL = 'https://verifiedid.co/trucheck/privacy-policy'

const ExternalLink = ({ href, className = '', children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-blue-700 underline ${className}`}
    >
        {children}
    </a>
)

const TermsOfUseLink = ({ className }) => (
    <ExternalLink href={TERMS_OF_USE_URL} className={className}>VerifiedID Terms of Use</ExternalLink>
)

const PrivacyPolicyLink = ({ className, children = 'TruCheck™ Privacy Policy' }) => (
    <ExternalLink href={PRIVACY_POLICY_URL} className={className}>{children}</ExternalLink>
)

const FcraDisclosureContent = ({ company }) => (
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p className="font-semibold text-gray-800 italic">PLEASE READ CAREFULLY:</p>
        <p>
            <span className="font-semibold">{company?.name ?? '[Employer/Client Name]'} (the "Company")</span> may
            obtain an identity verification report (which is a form of "consumer report") about you from{' '}
            <span className="font-semibold">VerifiedID LLC and its authorized agents</span> (acting as a
            third-party consumer reporting agency) for employment purposes, specifically as a prerequisite
            for interview scheduling, shortlisting, and identity authentication.
        </p>
        <p>
            As part of this "TruCheck" process, you will be the subject of a report which includes
            information about your identity, personal characteristics, and professional credentials.
            These reports may contain:
        </p>
        <ul className="list-disc pl-5 space-y-2">
            <li>
                <strong>Identity Authentication:</strong> Validation of government-issued identification.
            </li>
            <li>
                <strong>Employment Eligibility Verification:</strong> Review of citizenship, residency, or
                visa status (e.g., I-94, I-797, EAD) to determine{' '}
                <strong>eligibility for placement or employment</strong>.
            </li>
            <li>
                <strong>Historical Document Cross-Matching:</strong> Comparative cross-verification and
                reconciliation of your live session submissions against any prior identity, credential,
                or work authorization documents previously provided by you to the Company and
                uploaded to this system.
            </li>
            <li>
                <strong>Biometric Comparison:</strong> A facial "Match Selfie" compared against your provided
                identification.
            </li>
            <li>
                <strong>Identifier Verification:</strong> Confirmation of identifiers such as email and the last 4 digits of your SSN.
            </li>
            <li className="space-y-2">
                <p>
                    <strong>TruCheck™ Digital Card Generation &amp; Redaction Policy:</strong> Upon achieving
                    a "Clear" verification status, the creation and hosting of a tamper-proof TruCheck™ Digital
                    Card displaying your verified live photo, full legal name, U.S. employability status
                    indicator, check date/location, and verification reference/QR code.
                </p>
                <p>
                    <strong>Privacy &amp; Anti-Discrimination Redaction Notice:</strong> In compliance with the
                    Age Discrimination in Employment Act (ADEA), Title VII, and INA § 274B anti-discrimination
                    rules, sensitive parameters—including your specific Date/Year of Birth, Social Security
                    Number (SSN last-4), raw government ID image captures, and specific visa type labels—are
                    strictly redacted (----) on client-facing verification cards.
                </p>
                <p>
                    Full identifiers are retained solely on CRA audit reports accessible to you and authorized
                    compliance personnel.
                </p>
            </li>
        </ul>
        <p>
            This check is conducted to ensure the integrity of the hiring process, guarantee multi-source
            document data consistency, and protect against identity fraud.
        </p>
        <div className="bg-gray-100 rounded-lg p-3 text-sm space-y-1 border border-gray-200">
            <p className="font-semibold text-gray-800">VerifiedID LLC is located at:</p>
            <p>11180 State Bridge Road, Suite 303, Alpharetta, GA, 30022, USA</p>
        </div>
    </div>
)

const SummaryOfRightsContent = () => (
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
            The federal Fair Credit Reporting Act (FCRA) promotes the accuracy, fairness, and privacy
            of information in the files of consumer reporting agencies.
        </p>
        <ul className="list-disc pl-5 space-y-2">
            <li>
                <strong>You must be told if information in your file has been used against you.</strong>{' '}
                Anyone who uses a consumer report to deny your application for employment—including
                the decision to not proceed with an interview—must tell you and provide the contact
                information of the agency <strong>(VerifiedID LLC and its authorized agents)</strong> that
                provided the report.
            </li>
            <li>
                <strong>You have the right to know what is in your file.</strong> You may request and
                obtain all the information about you in the files of a consumer reporting agency.
            </li>
            <li>
                <strong>You have the right to dispute incomplete or inaccurate information.</strong> If
                you identify information in your file that is inaccurate, the agency must investigate.
            </li>
            <li>
                <strong>You must give your consent for reports to be provided to employers.</strong>
            </li>
        </ul>
        <p>
            For more information, visit:{' '}
            <ExternalLink
                href="https://files.consumerfinance.gov/f/201504_cfpb_summary_your-rights-under-fcra.pdf"
                className="break-all"
            >
                CFPB FCRA Summary of Rights
            </ExternalLink>.
        </p>
    </div>
)

const DataVerificationContent = ({ company }) => (
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
            I authorize <span className="font-semibold">{company?.name ?? '[Employer/Client Name]'}</span>{' '}
            and <span className="font-semibold">VerifiedID LLC (including its authorized agents)</span> to
            access, process, and cross-verify my personal information using my live provided
            government-issued documents, non-public personal parameters (such as the last 4 digits of my
            SSN), and any historical identity, corporate onboarding, or eligibility documents previously
            provided by me to{' '}
            <span className="font-semibold">{company?.name ?? '[Employer/Client Name]'}</span>.
        </p>
        <p>
            I acknowledge and agree that my use of this platform is subject to the <TermsOfUseLink /> and{' '}
            <PrivacyPolicyLink />.
        </p>
        <p>
            Furthermore, upon achieving a <strong>"Clear"</strong> status, I expressly authorize VerifiedID
            LLC to generate, host, and issue a digital <strong>TruCheck™ Card</strong> containing my verified
            live selfie, full legal name, employability status, check date/location, and unique Reference
            Number/QR code (with sensitive DOB, SSN, and raw ID images redacted pursuant to ADEA and INA §
            274B privacy standards), and share this digital credential with{' '}
            <span className="font-semibold">{company?.name ?? '[Employer/Client Name]'}</span> and its
            authorized end-client interviewers to confirm my identity during subsequent evaluation and
            interview stages.
        </p>
    </div>
)

const BiometricNoticeContent = () => (
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p className="font-semibold text-gray-800 italic">
            PLEASE READ CAREFULLY: TruCheck™ captures and processes biometric data.
        </p>
        <ul className="list-disc pl-5 space-y-2">
            <li>
                <strong>Biometric Data Collected:</strong> Facial geometry data, mathematical facial maps, and
                motion metrics captured during your live photo upload and active session verification video.{' '}
                <strong>
                    Please note that this active session explicitly includes the capturing of a background
                    video as part of your live interaction.
                </strong>
            </li>
            <li>
                <strong>Specific Purpose:</strong> Confirming match compatibility with your provided
                government identification to eliminate application fraud and credential theft.
            </li>
            <li>
                <strong>Downstream Infrastructure:</strong> We may process your biometric elements utilizing
                highly secure, bank-grade backend verification subsystems under strict contractual data
                privacy standards.
            </li>
            <li className="space-y-2">
                <p>
                    <strong>Retention and Deletion:</strong> Biometric identifiers and facial maps are
                    completely purged from our active processing systems immediately upon the successful
                    execution or terminal failure of your identity check session.
                </p>
                <p>
                    In all events, raw mathematical biometric signatures are permanently destroyed within
                    twenty-four (24) hours of collection (aligning strictly with Illinois BIPA 740 ILCS
                    14/15(a)), or upon the termination of our operational relationship with the instructing
                    Enterprise Client, whichever occurs first.
                </p>
                <p>
                    Flat photographic reference media follows the tiered retention schedules set forth in
                    the <PrivacyPolicyLink>Privacy Policy</PrivacyPolicyLink>.
                </p>
            </li>
        </ul>
    </div>
)

const EsignContent = () => (
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
            I agree to conduct this identity verification process electronically. I acknowledge that I am
            able to view and save PDF documents on this device, and I consent to receive legal notices and
            disclosures (including any potential Adverse Action reports) via the email address provided in
            this application.
        </p>
    </div>
)

// `readOnly` (consent re-sign): the candidate's ORIGINAL answers are shown locked — the disclosures are
// not re-agreed, only the signature page is signed again — and the button just continues.
const LegalDisclosuresAndConsentHub = forwardRef(({ data, company, refToPrint, handleNext, readOnly = false }, ref) => {

    const [expandedSections, setExpandedSections] = useState(ALL_EXPANDED)
    const [agreedFcra, setAgreedFcra] = useState(data?.agreedFcra ?? false)
    const [agreedDataVerification, setAgreedDataVerification] = useState(data?.agreedDataVerification ?? false)
    const [agreedBiometric, setAgreedBiometric] = useState(data?.agreedBiometric ?? false)
    const [agreedEsign, setAgreedEsign] = useState(data?.agreedEsign ?? false)
    const [requestEmailCopyDispute, setRequestEmailCopyDispute] = useState(data?.requestEmailCopyDispute ?? false)
    const [error, setError] = useState('')
    const [pendingNext, setPendingNext] = useState(false)

    const allChecked = agreedFcra && agreedDataVerification && agreedBiometric && agreedEsign

    // After expanding all sections and re-rendering, proceed to next step.
    // useEffect fires after React commits DOM changes, guaranteeing all
    // section content is present in refToPrint before handleNext runs.
    useEffect(() => {
        if (!pendingNext) return
        setPendingNext(false)
        handleNext()
    }, [pendingNext]) // eslint-disable-line react-hooks/exhaustive-deps

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            if (!allChecked) {
                setError('Please acknowledge all disclosures to proceed')
                return {
                    isValid: false,
                    value: null,
                }
            }
            setError('')
            return {
                isValid: true,
                value: {
                    agreedFcra,
                    agreedDataVerification,
                    agreedBiometric,
                    agreedEsign,
                    requestEmailCopyDispute,
                },
            }
        },
    }))

    const toggleSection = (id) => {
        setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const getSectionContent = (id) => {
        switch (id) {
            case 'fcra':
                return (
                    <FcraDisclosureContent
                        company={company}
                    />
                )
            case 'universal_rights':
                return (
                    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={requestEmailCopyDispute}
                                onChange={(e) => setRequestEmailCopyDispute(e.target.checked)}
                                disabled={readOnly}
                                className="mt-1.5 h-4 w-4 flex-shrink-0"
                            />
                            <span>
                                I request to receive a copy of my completed <strong>TruCheck</strong> report via email.
                            </span>
                        </label>
                        <p>
                            <strong>Notice of Dispute:</strong> If you believe your identity or eligibility
                            report contains inaccurate or incorrectly processed document information, you have
                            the right to dispute the accuracy of the report by contacting{' '}
                            <a
                                href="mailto:disputes.trucheck@verifiedid.co"
                                className="text-blue-700 underline break-all"
                            >
                                disputes.trucheck@verifiedid.co
                            </a>
                            . VerifiedID LLC will conduct a formal reinvestigation of your data{' '}
                            <strong>at no cost to you</strong>.
                        </p>
                    </div>
                )
            case 'summary':
                return <SummaryOfRightsContent />
            case 'data_verification':
                return <DataVerificationContent company={company} />
            case 'biometric':
                return <BiometricNoticeContent />
            case 'esign':
                return (
                    <div className="space-y-6">
                        <EsignContent />

                        <div className="space-y-5 border-t border-gray-200 pt-6 text-gray-700 text-base leading-relaxed">
                            <div className="space-y-1">
                                <h2 className="text-lg font-bold text-gray-800">Candidate Consent Action</h2>
                                <p>
                                    Please review and check each mandatory authorization box below to activate the
                                    system verification workflow.
                                </p>
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreedFcra}
                                    onChange={(e) => { setAgreedFcra(e.target.checked); setError('') }}
                                    disabled={readOnly}
                                    className="mt-1.5 h-4 w-4 flex-shrink-0"
                                />
                                <span>
                                    <strong>FCRA &amp; Disclosure Authorization:</strong> I acknowledge that I have
                                    received and read the FCRA Disclosure and the CFPB Summary of Rights under the FCRA. I
                                    authorize <strong>{company?.name ?? '[Employer/Client Name]'}</strong> and{' '}
                                    <strong>VerifiedID LLC</strong> (acting as a consumer reporting agency) to obtain
                                    identity and employability consumer reports—including the cross-verification of live
                                    assets against company-held historic candidate records—as described.
                                </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreedDataVerification}
                                    onChange={(e) => { setAgreedDataVerification(e.target.checked); setError('') }}
                                    disabled={readOnly}
                                    className="mt-1.5 h-4 w-4 flex-shrink-0"
                                />
                                <span>
                                    <strong>Data, Terms &amp; TruCheck™ Card Consent:</strong> I agree to the{' '}
                                    <TermsOfUseLink className="font-semibold" /> and{' '}
                                    <PrivacyPolicyLink className="font-semibold" />. I authorize{' '}
                                    <strong>VerifiedID LLC</strong> to process my personal data (including SSN
                                    verification) and cross-verify live records against historical credentials. Upon
                                    achieving a <strong>"Clear"</strong> verification result, I explicitly consent to the
                                    creation, hosting, and sharing of my <strong>TruCheck™ Digital Card</strong> (featuring
                                    my verified live selfie, legal name, employability status, and verification metrics,
                                    with DOB/SSN redacted for EEO compliance) with{' '}
                                    <strong>{company?.name ?? '[Employer/Client Name]'}</strong> and its client
                                    evaluators for interview identity validation. I also authorize{' '}
                                    <strong>VerifiedID LLC</strong> to disclose my information to federal and state
                                    government agencies where permitted or required by law, as described in the{' '}
                                    <PrivacyPolicyLink className="font-semibold" />.
                                </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreedBiometric}
                                    onChange={(e) => { setAgreedBiometric(e.target.checked); setError('') }}
                                    disabled={readOnly}
                                    className="mt-1.5 h-4 w-4 flex-shrink-0"
                                />
                                <span>
                                    <strong>Biometric Privacy Consent:</strong> I have read the Biometric Information
                                    Privacy Notice and the{' '}
                                    <PrivacyPolicyLink className="font-semibold">
                                        VerifiedID – TruCheck Privacy Policy
                                    </PrivacyPolicyLink>. I provide my express written consent for VerifiedID to process
                                    my facial geometry, session video, and background video recordings solely to match my
                                    physical appearance against my provided government identification. I understand my
                                    raw biometric mathematical templates will be destroyed within 24 hours of my check,
                                    while flat media files will follow the tiered retention timelines outlined in the
                                    Privacy Policy.
                                </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreedEsign}
                                    onChange={(e) => { setAgreedEsign(e.target.checked); setError('') }}
                                    disabled={readOnly}
                                    className="mt-1.5 h-4 w-4 flex-shrink-0"
                                />
                                <span>
                                    <strong>Electronic Records &amp; ESIGN Consent:</strong> I agree to conduct this
                                    identity verification process electronically. I acknowledge that I am able to view
                                    and save PDF documents on this device, and I consent to receive legal notices and
                                    disclosures via email.
                                </span>
                            </label>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div ref={refToPrint} className="w-full flex items-center justify-center px-2 sm:px-4 py-6 sm:py-10">
            <div className="max-w-3xl w-full min-w-0 bg-white shadow-xl rounded-2xl p-4 sm:p-8 space-y-6">

                <div className="space-y-2">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legal Disclosures &amp; Consent</h1>
                    <p className="text-gray-700 text-base leading-relaxed">
                        VerifiedID is committed to transparency. Please review the following disclosures and
                        provide your consent to continue. Your use of this service is governed by the{' '}
                        <TermsOfUseLink /> and <PrivacyPolicyLink />.
                    </p>
                </div>

                <div className="space-y-2">
                    {SECTIONS.map((section) => {
                        const isOpen = expandedSections[section.id]
                        return (
                            <div key={section.id} className="border border-gray-200 rounded-xl overflow-hidden">
                                <button
                                    onClick={() => toggleSection(section.id)}
                                    className="w-full flex items-center justify-between gap-2 px-4 sm:px-5 py-4 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                                >
                                    <span className={`font-semibold text-base ${isOpen ? 'text-blue-700' : 'text-gray-800'}`}>
                                        {section.label}
                                    </span>
                                    <span className={`ml-2 sm:ml-4 flex-shrink-0 text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                                        ▾
                                    </span>
                                </button>
                                {isOpen && (
                                    <div className="px-4 sm:px-5 pb-5 border-t border-gray-100">
                                        <div className="pt-4">
                                            {getSectionContent(section.id)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {readOnly && (
                    <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        These are the acknowledgements you provided when you first signed. They stay the same —
                        only the signature page needs to be signed again.
                    </p>
                )}

                {error && (
                    <p className="text-base text-red-700">{error}</p>
                )}

                <div className="text-center">
                    <button
                        onClick={() => {
                            if (!allChecked) {
                                setError('Please acknowledge all disclosures to proceed')
                                return
                            }
                            setError('')
                            // Ensure every section is expanded so refToPrint captures full content,
                            // then proceed after React commits the DOM update.
                            setExpandedSections(ALL_EXPANDED)
                            setPendingNext(true)
                        }}
                        disabled={!allChecked}
                        className="w-full sm:w-auto px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base sm:text-lg shadow-md transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        {readOnly ? 'Continue' : 'Acknowledge and Continue'}
                    </button>
                </div>

            </div>
        </div>
    )

})

export default LegalDisclosuresAndConsentHub
